<template>
  <!-- ใช้ layout หลักพร้อม side menu -->
  <template-layout>
    <div class="py-12 px-4">
      <!-- ============================== -->
      <!-- หัวข้อหน้า                        -->
      <!-- ============================== -->
      <nav class="mb-4 flex flex-wrap justify-between items-center">
        <h1 class="text-2xl text-theme-1 font-medium">
          ทดสอบ Linkage Center 2 API
        </h1>
      </nav>

      <!-- ============================== -->
      <!-- ฟอร์มค้นหา                       -->
      <!-- ============================== -->
      <div class="box shadow-md py-6 px-8 mb-5">
        <div class="grid grid-cols-12 gap-4 items-end">
          <!-- ช่องกรอกเลขบัตรประชาชน 13 หลัก -->
          <div class="col-span-12 md:col-span-6 lg:col-span-4">
            <label
              for="target-pid-input"
              class="form-label text-theme-98"
            >
              เลขบัตรประจำตัวประชาชน (13 หลัก)
              <span class="text-theme-5">*</span>
            </label>
            <input
              id="target-pid-input"
              type="text"
              class="form-control placeholder-theme-19"
              placeholder="กรุณากรอกเลขบัตรประชาชน 13 หลัก"
              maxlength="13"
              v-model="targetPID"
              @input="onlyNumber"
            />
          </div>

          <!-- ปุ่มค้นหา — disabled ถ้าเลขไม่ครบ 13 หลัก หรือกำลังโหลด -->
          <div class="col-span-12 md:col-span-3 lg:col-span-2">
            <button
              type="button"
              class="btn btn-primary w-full font-medium"
              :disabled="isLoading || targetPID.length !== 13"
              @click="searchPerson"
            >
              <!-- แสดง loading spinner ขณะรอ response -->
              <LoadingIcon
                v-if="isLoading"
                icon="oval"
                class="w-5 h-5 mr-2"
              />
              ค้นหา
            </button>
          </div>
        </div>
      </div>

      <!-- ============================== -->
      <!-- แสดงผลลัพธ์ JSON ดิบ              -->
      <!-- ============================== -->
      <div v-if="resultData" class="box shadow-md py-6 px-8 mb-5">
        <h2 class="text-lg text-theme-1 font-medium mb-3">
          ผลลัพธ์จาก API
        </h2>
        <!-- แสดง JSON response แบบ formatted -->
        <pre class="bg-gray-100 dark:bg-dark-2 rounded-md p-4 overflow-x-auto text-sm whitespace-pre-wrap break-words">{{ formattedResult }}</pre>
      </div>
    </div>
  </template-layout>
</template>

<script setup>
/**
 * หน้าทดสอบ Linkage Center 2 API
 *
 * ใช้สำหรับดึงข้อมูลทะเบียนราษฎรจาก DOPA ผ่าน backend proxy
 * ต้อง login ด้วย ThaID เท่านั้น (เพื่อให้ได้ access_token + id_token จาก Keycloak Broker API)
 *
 * Flow:
 * 1. onMounted — ดึง ThaID token จาก Keycloak Broker API
 *    ถ้าไม่ได้ login ด้วย ThaID → แสดง modal แจ้งเตือน + ปุ่มไปหน้าหลัก
 * 2. กรอกเลขบัตร 13 หลัก → กดค้นหา
 * 3. POST /api/dc/personalInfo ด้วย access_token, id_token, targetPID, is_dev=false
 *    (time-based token จะถูก inject อัตโนมัติโดย axios interceptor)
 * 4. แสดง JSON response ดิบ หรือ error modal
 */

import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";
import Swal from "sweetalert2";
import TemplateLayout from "@/layouts/side-menu/Main.vue";
import getThaidToken from "@/utils/get-thaid-token.js";

const router = useRouter();

// ============================
// State
// ============================

// เลขบัตรประชาชนเป้าหมาย (13 หลัก)
const targetPID = ref("");

// สถานะกำลังโหลด
const isLoading = ref(false);

// ข้อมูล response จาก API (null = ยังไม่ได้ค้นหา)
const resultData = ref(null);

// ThaID tokens ที่ได้จาก Keycloak Broker API
// { access_token, id_token, ... } หรือ null ถ้าไม่ได้ login ด้วย ThaID
const thaidTokens = ref(null);

// ============================
// Computed
// ============================

// แปลง resultData เป็น JSON string สวยงาม
const formattedResult = computed(() => {
  if (!resultData.value) return "";
  return JSON.stringify(resultData.value, null, 2);
});

// ============================
// SweetAlert2 mixin (ใช้ style เดียวกับทั้งโปรเจค)
// ============================
const swalCustom = Swal.mixin({
  customClass: {
    confirmButton: "btn btn-primary m-1",
    cancelButton: "btn btn-outline-secondary m-1",
  },
  buttonsStyling: false,
});

// ============================
// Lifecycle
// ============================

onMounted(async () => {
  /**
   * ดึง ThaID token จาก Keycloak Broker API
   * ถ้า user login ด้วย ThaID → จะได้ { access_token, id_token, ... }
   * ถ้า user login ด้วย username/password → จะได้ null
   */
  const tokens = await getThaidToken();

  if (!tokens) {
    // ไม่ได้ login ด้วย ThaID → แสดง modal แจ้งเตือน
    swalCustom.fire({
      icon: "warning",
      title: "กรุณาเข้าสู่ระบบด้วย ThaID",
      html: "คุณต้องเข้าสู่ระบบด้วย <b>ThaID</b> เพื่อใช้งานหน้านี้<br>กรุณาออกจากระบบแล้วเข้าสู่ระบบใหม่โดยเลือก ThaID",
      confirmButtonText: "ไปหน้าหลัก",
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(() => {
      router.push({ name: "landing-page" });
    });
    return;
  }

  // เก็บ tokens ไว้ใช้ตอนเรียก API
  thaidTokens.value = tokens;
});

// ============================
// Methods
// ============================

/**
 * กรองให้กรอกได้เฉพาะตัวเลข
 */
const onlyNumber = () => {
  targetPID.value = targetPID.value.replace(/[^\d]/g, "");
};

/**
 * เรียก Linkage Center 2 API เพื่อดึงข้อมูลทะเบียนราษฎร
 *
 * POST /api/dc/personalInfo
 * Body: { access_token, id_token, targetPID, is_dev }
 *
 * หมายเหตุ: field "token" (time-based SHA-256 hash) จะถูก inject
 * อัตโนมัติโดย axios interceptor (src/utils/axios-interceptor.js)
 */
const searchPerson = async () => {
  // ตรวจสอบความยาว PID
  if (targetPID.value.length !== 13) {
    swalCustom.fire({
      icon: "warning",
      text: "กรุณากรอกเลขบัตรประชาชนให้ครบ 13 หลัก",
      confirmButtonText: "ปิด",
    });
    return;
  }

  // ตรวจสอบว่ามี ThaID tokens
  if (!thaidTokens.value) {
    swalCustom.fire({
      icon: "warning",
      title: "ไม่พบ ThaID Token",
      text: "กรุณาเข้าสู่ระบบด้วย ThaID ก่อนใช้งาน",
      confirmButtonText: "ปิด",
    });
    return;
  }

  isLoading.value = true;
  resultData.value = null;

  try {
    // สร้าง request body ตาม spec
    const requestBody = {
      access_token: thaidTokens.value.access_token,
      id_token: thaidTokens.value.id_token,
      targetPID: targetPID.value,
      is_dev: false,
    };

    // เรียก API — ใช้ VUE_APP_BASE_API เป็น base URL
    // URL เต็ม: https://mso-logbook.m-society.go.th/api/dc/personalInfo
    const response = await axios.post(
      `${process.env.VUE_APP_BASE_API}dc/personalInfo`,
      requestBody
    );

    // ตรวจสอบ response จาก API
    if (response.data && response.data.success === false) {
      // API ส่ง success: false → แสดง error modal
      const err = response.data.error || {};
      swalCustom.fire({
        icon: "error",
        title: err.code || "เกิดข้อผิดพลาด",
        text: err.message || "ไม่สามารถดึงข้อมูลได้",
        footer: err.detail ? `<small>${err.detail}</small>` : undefined,
        confirmButtonText: "ปิด",
      });
      return;
    }

    // สำเร็จ — เก็บ response ทั้งหมดไว้แสดง
    resultData.value = response.data;
  } catch (error) {
    // Axios error (network error, 4xx, 5xx)
    const errData = error.response?.data;
    const errMessage =
      errData?.error?.message ||
      errData?.message ||
      error.message ||
      "เกิดข้อผิดพลาดในการเชื่อมต่อ";
    const errCode =
      errData?.error?.code || `HTTP ${error.response?.status || "Network Error"}`;

    swalCustom.fire({
      icon: "error",
      title: errCode,
      text: errMessage,
      confirmButtonText: "ปิด",
    });
  } finally {
    isLoading.value = false;
  }
};
</script>
